import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    useDeliveryReport,
    useDeliveryReportSummary,
    useDeliveryReportByTeam,
    useDeliveryReportByFlyer,
    useDeliveryReportTimeline,
    useDeliveryReportLineItems,
    type DeliveryReportRequest,
    type DeliveryReportLineItem,
    type UpdateDeliveryFlyerTypeBody,
    type BulkReassignDeliveryFlyerTypeRequest,
    fetchAdminKpis,
    updateDeliveryFlyerType,
    bulkReassignDeliveryFlyerType
} from "../api/reports";
import { useCampaigns, useFlyerTypes } from "../api/campaigns";
import { useAllTeamsForReports } from "../api/teams";
import { ReportFilters, defaultReportFilters } from "./components/ReportFilters";
import { ReportTable } from "./components/ReportTable";
import { ReportCharts } from "./components/ReportCharts";
import { showError, showSuccess } from "../shared/toast";
import "./delivery-reports-page.css";

export function deliveryFlyerKey(row: DeliveryReportLineItem): string {
    if (row.campaignFlyerId) {
        return `cf:${row.campaignFlyerId}`;
    }
    if (row.flyerTypeId) {
        return `ft:${row.flyerTypeId}`;
    }
    return "";
}

export function parseFlyerKeyForPatch(key: string): UpdateDeliveryFlyerTypeBody {
    if (key.startsWith("cf:")) {
        return { targetCampaignFlyerId: key.slice(3) };
    }
    if (key.startsWith("ft:")) {
        return { flyerTypeId: key.slice(3) };
    }
    return {};
}

export function parseKeyForBulk(
    key: string,
    role: "source" | "target"
): Pick<
    BulkReassignDeliveryFlyerTypeRequest,
    "sourceFlyerTypeId" | "sourceCampaignFlyerId" | "targetFlyerTypeId" | "targetCampaignFlyerId"
> {
    if (!key || (!key.startsWith("cf:") && !key.startsWith("ft:"))) {
        return {};
    }
    const id = key.slice(3);
    if (role === "source") {
        return key.startsWith("cf:") ? { sourceCampaignFlyerId: id } : { sourceFlyerTypeId: id };
    }
    return key.startsWith("cf:") ? { targetCampaignFlyerId: id } : { targetFlyerTypeId: id };
}

export const DeliveryReportsPage = (): JSX.Element => {
    const queryClient = useQueryClient();
    const [filters, setFilters] = useState<DeliveryReportRequest>(defaultReportFilters);
    const [viewMode, setViewMode] = useState<"table" | "charts">("table");
    const [groupBy, setGroupBy] = useState<"team" | "flyer" | "timeline">("flyer");
    const [flyerCorrectionOpen, setFlyerCorrectionOpen] = useState(false);
    const [pendingFlyerTypeByDelivery, setPendingFlyerTypeByDelivery] = useState<Record<string, string>>({});
    const [bulkSourceKey, setBulkSourceKey] = useState("");
    const [bulkTargetKey, setBulkTargetKey] = useState("");
    const [bulkTargetCampaignId, setBulkTargetCampaignId] = useState("");

    const { data: summary } = useDeliveryReportSummary(filters);
    const { data: reportData = [] } = useDeliveryReport(filters);
    const { data: byTeamData = [] } = useDeliveryReportByTeam(filters);
    const { data: byFlyerData = [] } = useDeliveryReportByFlyer(filters);
    const { data: timelineData = [] } = useDeliveryReportTimeline(filters);
    const { data: campaigns = [] } = useCampaigns();
    const { data: teams = [] } = useAllTeamsForReports();
    const { data: kpis } = useQuery({
        queryKey: ["admin", "kpis"],
        queryFn: fetchAdminKpis,
        staleTime: 60 * 1000
    });

    // Share of the municipality's households reached. One decimal keeps a large kommun's
    // slow progress visible instead of rounding it to a flat 0 %.
    const householdShare = useMemo(() => {
        if (!summary?.totalHouseholds) {
            return null;
        }
        return ((summary.reachedHouseholds / summary.totalHouseholds) * 100).toFixed(1);
    }, [summary?.reachedHouseholds, summary?.totalHouseholds]);

    const lineItemsQuery = useDeliveryReportLineItems(
        filters.campaignId && flyerCorrectionOpen ? filters : undefined
    );
    const effectiveTargetCampaignId = bulkTargetCampaignId || filters.campaignId;
    const campaignFlyersQuery = useFlyerTypes(
        effectiveTargetCampaignId && flyerCorrectionOpen ? effectiveTargetCampaignId : null
    );

    const targetFlyerOptions = useMemo(
        () =>
            (campaignFlyersQuery.data ?? []).map((f) =>
                f.flyerTypeId
                    ? { key: `ft:${f.flyerTypeId}`, label: f.flyerType?.name ?? f.name }
                    : { key: `cf:${f.id}`, label: f.name }
            ),
        [campaignFlyersQuery.data]
    );

    const sourceOptions = useMemo(() => {
        const rows = lineItemsQuery.data ?? [];
        const seen = new Set<string>();
        const out: { key: string; label: string }[] = [];
        for (const row of rows) {
            const k = deliveryFlyerKey(row);
            if (!k || seen.has(k)) {
                continue;
            }
            seen.add(k);
            out.push({ key: k, label: row.flyerName || k });
        }
        return out.sort((a, b) => a.label.localeCompare(b.label, "sv"));
    }, [lineItemsQuery.data]);

    const previewMatchCount = useMemo(() => {
        if (!bulkSourceKey) {
            return 0;
        }
        return (lineItemsQuery.data ?? []).filter((r) => deliveryFlyerKey(r) === bulkSourceKey).length;
    }, [bulkSourceKey, lineItemsQuery.data]);

    const updateFlyerMutation = useMutation({
        mutationFn: ({ deliveryId, ...body }: { deliveryId: string } & UpdateDeliveryFlyerTypeBody) =>
            updateDeliveryFlyerType(deliveryId, body),
        onSuccess: async (_, { deliveryId }) => {
            showSuccess("Flygblad uppdaterat");
            setPendingFlyerTypeByDelivery((prev) => {
                const next = { ...prev };
                delete next[deliveryId];
                return next;
            });
            await queryClient.invalidateQueries({ queryKey: ["reports", "deliveries"] });
        },
        onError: (e) => {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError("Kunde inte uppdatera flygblad", msg);
        }
    });

    const bulkReassignMutation = useMutation({
        mutationFn: async () => {
            if (!filters.campaignId) {
                throw new Error("Ingen kampanj vald");
            }
            const src = parseKeyForBulk(bulkSourceKey, "source");
            const tgt = parseKeyForBulk(bulkTargetKey, "target");
            return bulkReassignDeliveryFlyerType({
                campaignId: filters.campaignId,
                teamId: filters.teamId,
                from: filters.from,
                to: filters.to,
                flyerTypeFilterId: filters.flyerTypeId,
                onlyMine: filters.onlyMine,
                targetCampaignId: bulkTargetCampaignId || undefined,
                ...src,
                ...tgt
            });
        },
        onSuccess: async (data) => {
            showSuccess(`${data.affectedCount} leveranser uppdaterades`);
            setBulkSourceKey("");
            setBulkTargetKey("");
            await queryClient.invalidateQueries({ queryKey: ["reports", "deliveries"] });
        },
        onError: (e) => {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError("Massbyte misslyckades", msg);
        }
    });

    const handleExport = () => {
        const params = new URLSearchParams();
        if (filters.teamId) params.append("teamId", filters.teamId);
        if (filters.campaignId) params.append("campaignId", filters.campaignId);
        if (filters.flyerTypeId) params.append("flyerTypeId", filters.flyerTypeId);
        if (filters.from) params.append("from", filters.from);
        if (filters.to) params.append("to", filters.to);
        if (filters.onlyMine) params.append("onlyMine", "true");

        window.open(`/api/reports/deliveries/export?${params.toString()}`, "_blank");
    };

    const getDisplayData = () => {
        switch (groupBy) {
            case "team":
                return byTeamData;
            case "flyer":
                return byFlyerData;
            case "timeline":
                // Convert timeline data to report row format
                return timelineData.map((item) => ({
                    flyerName: new Date(item.date).toISOString(),
                    deliveries: item.deliveries,
                    quantitySum: item.quantity,
                }));
            default:
                return reportData;
        }
    };

    return (
        <section className="delivery-reports-page" aria-label="Rapporter om utdelade mängder">
            <div className="delivery-reports-page__header">
                <h2>Rapporter om utdelade mängder</h2>
                <div className="delivery-reports-page__actions">
                    <button
                        className="delivery-reports-page__export-button"
                        onClick={handleExport}
                    >
                        Exportera CSV
                    </button>
                </div>
            </div>

            <ReportFilters
                filters={filters}
                onFiltersChange={setFilters}
                campaigns={campaigns}
                teams={teams}
            />

            {filters.campaignId ? (
                <p className="delivery-reports-page__flyer-correction-trigger">
                    <button
                        type="button"
                        className="delivery-reports-page__flyer-correction-toggle"
                        onClick={() =>
                            setFlyerCorrectionOpen((o) => {
                                const next = !o;
                                if (!next) {
                                    setPendingFlyerTypeByDelivery({});
                                    setBulkSourceKey("");
                                    setBulkTargetKey("");
                                    setBulkTargetCampaignId("");
                                }
                                return next;
                            })
                        }
                        aria-expanded={flyerCorrectionOpen}
                    >
                        {flyerCorrectionOpen ? "Dölj rättning av flygblad" : "Rätta fel registrerat flygblad…"}
                    </button>
                </p>
            ) : null}

            {flyerCorrectionOpen && filters.campaignId ? (
                <div className="delivery-reports-page__flyer-correction-panel">
                    <div className="delivery-reports-page__flyer-correction-panel-header">
                        <h3 className="delivery-reports-page__line-items-title">Rätta flygblad</h3>
                        <button
                            type="button"
                            className="delivery-reports-page__flyer-correction-close"
                            onClick={() => {
                                setFlyerCorrectionOpen(false);
                                setPendingFlyerTypeByDelivery({});
                                setBulkSourceKey("");
                                setBulkTargetKey("");
                                setBulkTargetCampaignId("");
                            }}
                            aria-label="Stäng"
                        >
                            Stäng
                        </button>
                    </div>

                    <div className="delivery-reports-page__bulk-flyer">
                        <h4 className="delivery-reports-page__bulk-flyer-title">Byt för många leveranser</h4>
                        <p className="delivery-reports-page__bulk-flyer-help">
                            Använder samma filter som rapporten (kampanj, team, datum, ev. flygblad och
                            &quot;Visa bara mina&quot;). Alla matchande
                            utdelningar byts (inte bara de 2000 som visas här), högst 20&nbsp;000 per körning.
                        </p>
                        <div className="delivery-reports-page__bulk-flyer-row">
                            <label className="delivery-reports-page__bulk-flyer-label" htmlFor="bulk-source-flyer">
                                Felaktigt flygblad (nu)
                            </label>
                            <select
                                id="bulk-source-flyer"
                                className="delivery-reports-page__line-items-select"
                                value={bulkSourceKey}
                                onChange={(e) => setBulkSourceKey(e.target.value)}
                            >
                                <option value="">Välj källflygblad…</option>
                                {sourceOptions.map((o) => (
                                    <option key={o.key} value={o.key}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="delivery-reports-page__bulk-flyer-row">
                            <label className="delivery-reports-page__bulk-flyer-label" htmlFor="bulk-target-campaign">
                                Målkampanj
                            </label>
                            <select
                                id="bulk-target-campaign"
                                className="delivery-reports-page__line-items-select"
                                value={bulkTargetCampaignId}
                                onChange={(e) => {
                                    setBulkTargetCampaignId(e.target.value);
                                    setBulkTargetKey("");
                                }}
                            >
                                <option value="">Samma kampanj</option>
                                {campaigns
                                    .filter((c) => c.id !== filters.campaignId && !c.isArchived)
                                    .map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className="delivery-reports-page__bulk-flyer-row">
                            <label className="delivery-reports-page__bulk-flyer-label" htmlFor="bulk-target-flyer">
                                Ska vara
                            </label>
                            <select
                                id="bulk-target-flyer"
                                className="delivery-reports-page__line-items-select"
                                value={bulkTargetKey}
                                onChange={(e) => setBulkTargetKey(e.target.value)}
                            >
                                <option value="">Välj målflygblad…</option>
                                {targetFlyerOptions.map((o) => (
                                    <option key={o.key} value={o.key}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {targetFlyerOptions.length === 0 && !campaignFlyersQuery.isLoading ? (
                            <p className="delivery-reports-page__line-items-error">
                                Inga kampanjflygblad hittades. Kontrollera att kampanjen har minst ett flygblad.
                            </p>
                        ) : null}
                        <div className="delivery-reports-page__bulk-flyer-actions">
                            <button
                                type="button"
                                className="delivery-reports-page__bulk-flyer-submit"
                                disabled={
                                    !bulkSourceKey ||
                                    !bulkTargetKey ||
                                    (!bulkTargetCampaignId && bulkSourceKey === bulkTargetKey) ||
                                    bulkReassignMutation.isPending
                                }
                                onClick={() => {
                                    const targetCampaignName = bulkTargetCampaignId
                                        ? (campaigns.find((c) => c.id === bulkTargetCampaignId)?.name ?? "annan kampanj")
                                        : null;
                                    const confirmMsg = targetCampaignName
                                        ? `Byt flygblad och flytta till kampanj "${targetCampaignName}" för alla leveranser som matchar filter och valt källflygblad? Minst ${previewMatchCount} rader i listan nedan; fler kan finnas utanför listan (max 20 000).`
                                        : `Byt flygblad för alla leveranser som matchar filter och valt källflygblad? Minst ${previewMatchCount} rader i listan nedan; fler kan finnas utanför listan (max 20 000).`;
                                    if (!window.confirm(confirmMsg)) {
                                        return;
                                    }
                                    bulkReassignMutation.mutate();
                                }}
                            >
                                {bulkReassignMutation.isPending ? "Uppdaterar…" : "Byt för alla matchande"}
                            </button>
                            {bulkSourceKey ? (
                                <span className="delivery-reports-page__bulk-flyer-preview">
                                    {previewMatchCount} rader i aktuell lista matchar källvalet.
                                </span>
                            ) : null}
                        </div>
                    </div>

                    <div className="delivery-reports-page__line-items">
                        <h4 className="delivery-reports-page__per-row-title">Enskilda rader (max 2000)</h4>
                        <p className="delivery-reports-page__line-items-help">
                            För enstaka undantag efter massbyte. Välj nytt flygblad och spara.
                        </p>
                        {lineItemsQuery.isLoading ? <p>Laddar leveranser…</p> : null}
                        {lineItemsQuery.isError ? (
                            <p className="delivery-reports-page__line-items-error">Kunde inte ladda leveransrader.</p>
                        ) : null}
                        {lineItemsQuery.data && lineItemsQuery.data.length > 0 ? (
                            <div className="delivery-reports-page__line-items-table-wrap">
                                <table className="delivery-reports-page__line-items-table">
                                    <thead>
                                        <tr>
                                            <th>Datum</th>
                                            <th>Adress</th>
                                            <th>Nuvarande flygblad</th>
                                            <th>Antal</th>
                                            <th>Nytt flygblad</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lineItemsQuery.data.map((row: DeliveryReportLineItem) => {
                                            const selected = pendingFlyerTypeByDelivery[row.id] ?? "";
                                            const currentKey = deliveryFlyerKey(row);
                                            const unchanged = !selected || selected === currentKey;
                                            return (
                                                <tr key={row.id}>
                                                    <td>{new Date(row.deliveredAt).toLocaleString("sv-SE")}</td>
                                                    <td>{row.addressLabel || row.addressPointId}</td>
                                                    <td>{row.flyerName}</td>
                                                    <td>{row.quantity}</td>
                                                    <td>
                                                        <select
                                                            className="delivery-reports-page__line-items-select"
                                                            value={selected}
                                                            onChange={(e) =>
                                                                setPendingFlyerTypeByDelivery((prev) => ({
                                                                    ...prev,
                                                                    [row.id]: e.target.value
                                                                }))
                                                            }
                                                            aria-label="Nytt flygblad"
                                                        >
                                                            <option value="">Välj nytt flygblad…</option>
                                                            {targetFlyerOptions.map((o) => (
                                                                <option key={o.key} value={o.key}>
                                                                    {o.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="delivery-reports-page__line-items-save"
                                                            disabled={unchanged || updateFlyerMutation.isPending}
                                                            onClick={() => {
                                                                if (!selected) {
                                                                    return;
                                                                }
                                                                updateFlyerMutation.mutate({
                                                                    deliveryId: row.id,
                                                                    ...parseFlyerKeyForPatch(selected)
                                                                });
                                                            }}
                                                        >
                                                            Spara
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : lineItemsQuery.data?.length === 0 && !lineItemsQuery.isLoading ? (
                            <p>Inga leveranser för aktuella filter.</p>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {summary && (
                <div className="delivery-reports-page__summary">
                    <div className="summary-card">
                        <p className="summary-card__label">Totalt antal utdelningar</p>
                        <p className="summary-card__value">{summary.totalDeliveries.toLocaleString()}</p>
                    </div>
                    <div className="summary-card">
                        <p className="summary-card__label">Totalt antal flygblad</p>
                        <p className="summary-card__value">{summary.totalQuantity.toLocaleString()}</p>
                    </div>
                    <div className="summary-card">
                        <p className="summary-card__label">Antal kampanjer</p>
                        <p className="summary-card__value">{summary.uniqueCampaigns}</p>
                    </div>
                    <div className="summary-card">
                        <p className="summary-card__label">Antal olika flygblad</p>
                        <p className="summary-card__value">{summary.uniqueFlyerTypes}</p>
                    </div>
                    <div className="summary-card">
                        <p className="summary-card__label">Hushåll vi nått</p>
                        <p className="summary-card__value">
                            {summary.reachedHouseholds.toLocaleString("sv-SE")}
                            {householdShare !== null && (
                                <span className="summary-card__share"> · {householdShare}%</span>
                            )}
                        </p>
                        <p className="summary-card__hint">
                            {summary.totalHouseholds != null
                                ? `av ${summary.totalHouseholds.toLocaleString("sv-SE")} i kommunen`
                                : "Välj kommun för att se andel"}
                        </p>
                    </div>
                </div>
            )}

            {kpis && kpis.unreportedRoGroupsCount > 0 && (
                <div className="delivery-reports-page__ro-alert">
                    <div className="ro-alert">
                        <span className="ro-alert__icon">⚠️</span>
                        <div className="ro-alert__content">
                            <p className="ro-alert__title">Orapporterade leveranser till RO</p>
                            <p className="ro-alert__message">
                                Det finns {kpis.unreportedRoGroupsCount} {kpis.unreportedRoGroupsCount === 1 ? "grupp" : "grupper"} med leveranser som väntar på att rapporteras till RO.
                            </p>
                        </div>
                        <Link to="/admin/ro-reporting" className="ro-alert__link">
                            Hantera RO-rapportering →
                        </Link>
                    </div>
                </div>
            )}

            <div className="delivery-reports-page__controls">
                <div className="delivery-reports-page__view-toggle">
                    <button
                        className={`toggle-button ${viewMode === "table" ? "active" : ""}`}
                        onClick={() => setViewMode("table")}
                    >
                        Tabell
                    </button>
                    <button
                        className={`toggle-button ${viewMode === "charts" ? "active" : ""}`}
                        onClick={() => setViewMode("charts")}
                    >
                        Grafer
                    </button>
                </div>
                {viewMode === "table" && (
                    <div className="delivery-reports-page__group-by">
                        <label htmlFor="group-by">Gruppera efter:</label>
                        <select
                            id="group-by"
                            value={groupBy}
                            onChange={(e) => setGroupBy(e.target.value as "team" | "flyer" | "timeline")}
                            className="group-by-select"
                        >
                            <option value="flyer">Flygblad</option>
                            <option value="team">Team</option>
                            <option value="timeline">Tidslinje</option>
                        </select>
                    </div>
                )}
            </div>

            {viewMode === "table" ? (
                <ReportTable data={getDisplayData()} groupBy={groupBy} />
            ) : (
                <ReportCharts
                    timelineData={timelineData}
                    byFlyerData={byFlyerData}
                    byTeamData={byTeamData}
                />
            )}
        </section>
    );
};